/**
 * Playbook data layer with a graceful fallback (mirrors the dashboard store).
 *
 * Every CRUD call tries the real server first (api.gamemybiz.com/api/playbook).
 * If that route isn't deployed yet (or offline), it falls back to the browser's
 * localStorage so you can CREATE and LOAD playbooks immediately with no error.
 * Once the server is deployed, the same calls hit it automatically.
 *
 * NOTE: YETI generation and image upload are NOT wrapped here — they run only
 * server-side (AI + S3), so those need the deployed server. Blank create, load,
 * edit-by-hand, and save all work locally via this fallback.
 */
import * as api from "../../api/playbook";

const KEY = "gmb_local_playbooks";

const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (e) {
    return [];
  }
};
const write = (arr) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch (e) {
    /* storage full / unavailable */
  }
};
const uid = () =>
  `local_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const stamp = () => new Date().toISOString();

export const GET_PLAYBOOKS = async () => {
  try {
    const res = await api.GET_PLAYBOOKS();
    // Books created while the server was unreachable live only in this
    // browser — keep them in the list next to the server's books so nothing
    // ever disappears from the library.
    const locals = read().filter((b) => String(b._id).startsWith("local_"));
    if (locals.length && Array.isArray(res.data)) {
      return { ...res, data: [...locals, ...res.data] };
    }
    return res;
  } catch (e) {
    return { data: read(), local: true };
  }
};

export const GET_PLAYBOOK = async (id) => {
  try {
    return await api.GET_PLAYBOOK(id);
  } catch (e) {
    const found = read().find((b) => b._id === id);
    if (found) return { data: found, local: true };
    throw e;
  }
};

export const CREATE_PLAYBOOK = async (data) => {
  try {
    return await api.CREATE_PLAYBOOK(data);
  } catch (e) {
    const now = stamp();
    const book = {
      _id: uid(),
      title: "Company Playbook",
      type: "Playbook",
      audience: { mode: "everyone", workspaceIds: [] },
      theme: { template: "modern", primary: "", secondary: "", gradient: false },
      sections: [],
      ...data,
      createdAt: now,
      updatedAt: now,
    };
    write([book, ...read()]);
    return { data: book, local: true };
  }
};

export const UPDATE_PLAYBOOK = async (id, data) => {
  try {
    return await api.UPDATE_PLAYBOOK(id, data);
  } catch (e) {
    let updated = null;
    const arr = read().map((b) => {
      if (b._id !== id) return b;
      updated = { ...b, ...data, updatedAt: stamp() };
      return updated;
    });
    // If it existed locally, persist. If not (a server book while offline),
    // keep a local copy so edits aren't lost.
    if (updated) write(arr);
    else {
      updated = { _id: id, ...data, updatedAt: stamp() };
      write([updated, ...read()]);
    }
    return { data: updated, local: true };
  }
};

export const DELETE_PLAYBOOK = async (id) => {
  try {
    return await api.DELETE_PLAYBOOK(id);
  } catch (e) {
    write(read().filter((b) => b._id !== id));
    return { data: { ok: true, _id: id }, local: true };
  }
};
